import { useState, useEffect, useCallback } from "react";
import { apiClient } from "../../App";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Check, ExternalLink, FileText, GitPullRequest, Loader2, Paperclip, RefreshCw, RotateCcw, Sparkles } from "lucide-react";
import { STATUS, CATEGORY, URGENCY, StatusPill, fullTime, ago } from "./helpDesk";

const EVENT_VERB = {
  created: "filed the request",
  edited: "edited the request",
  assigned: "changed the owner",
  approved: "approved it",
  sent_to_claude: "sent it to Claude",
  asked: "asked a question",
  declined: "declined it",
  done: "checked it off",
  reopened: "reopened it",
  attachment: "added a file",
  claude_started: "started",
  claude_reply: "replied",
  claude_failed: "hit a problem",
  claude_branch: "pushed a change",
  claude_pr_ready: "opened a pull request",
  shipped: "shipped",
  pr_closed: "closed the pull request",
};

function Attachment({ requestId, att }) {
  const [url, setUrl] = useState(null);
  const isImage = (att.content_type || "").startsWith("image/");

  useEffect(() => {
    if (!isImage) return undefined;
    let revoked = null;
    apiClient.get(`/help-desk/requests/${requestId}/attachments/${att.id}`, { responseType: "blob" })
      .then((r) => { revoked = URL.createObjectURL(r.data); setUrl(revoked); })
      .catch(() => {});
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [requestId, att.id, isImage]);

  const open = async () => {
    try {
      const r = await apiClient.get(`/help-desk/requests/${requestId}/attachments/${att.id}`, { responseType: "blob" });
      const href = URL.createObjectURL(r.data);
      window.open(href, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(href), 60000);
    } catch {
      toast.error("Couldn't open that file");
    }
  };

  return (
    <button type="button" onClick={open} className="group text-left" title={att.filename}>
      {isImage && url ? (
        <img src={url} alt={att.filename} className="h-20 w-28 object-cover rounded-md border border-border group-hover:opacity-90" />
      ) : (
        <span className="flex h-20 w-28 flex-col items-center justify-center gap-1 rounded-md border border-border px-2 text-[11px] text-muted-foreground">
          <FileText className="w-4 h-4" />
          <span className="truncate max-w-full">{att.filename}</span>
        </span>
      )}
    </button>
  );
}

export default function RequestDetail({ requestId, me, settings, people, onChanged }) {
  const [req, setReq] = useState(null);
  const [loading, setLoading] = useState(true);
  const [brief, setBrief] = useState("");
  const [briefDirty, setBriefDirty] = useState(false);
  const [assigneeId, setAssigneeId] = useState("");
  const [note, setNote] = useState("");
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(null);

  const isApprover = !!settings?.is_approver;

  const apply = useCallback((data, { keepBrief } = {}) => {
    setReq(data);
    if (!keepBrief) {
      setBrief(data.brief || "");
      setBriefDirty(false);
    }
    setAssigneeId(data.assignee?.id || "");
  }, []);

  const load = useCallback(async () => {
    try {
      const { data } = await apiClient.get(`/help-desk/requests/${requestId}`);
      apply(data, { keepBrief: briefDirty });
    } catch (e) {
      toast.error(e.response?.status === 404 ? "That request no longer exists" : "Couldn't load the request");
    } finally {
      setLoading(false);
    }
  }, [requestId, apply, briefDirty]);

  useEffect(() => { setLoading(true); setBriefDirty(false); }, [requestId]);
  useEffect(() => { load(); }, [requestId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Claude's draft lands a few seconds after filing — poll briefly for it.
  useEffect(() => {
    if (!req || req.ai_draft || !isApprover || req.status !== "new") return undefined;
    const age = Date.now() - new Date(req.created_at).getTime();
    if (age > 3 * 60 * 1000) return undefined;
    const t = setTimeout(load, 8000);
    return () => clearTimeout(t);
  }, [req, isApprover, load]);

  // While Claude is on it, keep the status fresh.
  useEffect(() => {
    if (!req || !["claude", "review"].includes(req.status)) return undefined;
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
  }, [req, load]);

  const act = async (action, extra = {}) => {
    setBusy(action);
    try {
      const body = { action, ...extra };
      if (isApprover && briefDirty) body.brief = brief;
      const { data } = await apiClient.post(`/help-desk/requests/${requestId}/action`, body);
      apply(data);
      setNote("");
      onChanged?.();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString?.() || "That didn't go through");
      return false;
    } finally {
      setBusy(null);
    }
  };

  const saveBrief = async () => {
    setBusy("brief");
    try {
      const { data } = await apiClient.patch(`/help-desk/requests/${requestId}`, { brief });
      apply(data);
      toast.success("Brief saved");
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString?.() || "Couldn't save the brief");
    } finally {
      setBusy(null);
    }
  };

  const redraft = async () => {
    setBusy("draft");
    try {
      const { data } = await apiClient.post(`/help-desk/requests/${requestId}/draft`);
      apply(data, { keepBrief: briefDirty });
      toast.success("Claude drafted a fresh brief");
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString?.() || "Claude couldn't draft right now");
    } finally {
      setBusy(null);
    }
  };

  const sync = async () => {
    setBusy("sync");
    try {
      const { data } = await apiClient.post(`/help-desk/requests/${requestId}/sync`);
      apply(data, { keepBrief: briefDirty });
      onChanged?.();
    } catch {
      toast.error("Couldn't reach GitHub");
    } finally {
      setBusy(null);
    }
  };

  const sendComment = async () => {
    if (!comment.trim()) return;
    setBusy("comment");
    try {
      const { data } = await apiClient.post(`/help-desk/requests/${requestId}/comments`, { text: comment });
      apply(data, { keepBrief: briefDirty });
      setComment("");
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail?.toString?.() || "Couldn't post that");
    } finally {
      setBusy(null);
    }
  };

  const attach = async (fileList) => {
    for (const f of Array.from(fileList || [])) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await apiClient.post(`/help-desk/requests/${requestId}/attachments`, fd);
        apply(data, { keepBrief: briefDirty });
      } catch (e) {
        toast.error(e.response?.data?.detail?.toString?.() || `Couldn't attach ${f.name}`);
      }
    }
  };

  if (loading && !req) {
    return <div className="flex items-center justify-center py-24 text-muted-foreground"><Loader2 className="w-5 h-5 animate-spin" /></div>;
  }
  if (!req) return null;

  const isRequester = req.requester?.id === me?.id;
  const isAssignee = req.assignee?.id === me?.id;
  const open = !["done", "declined"].includes(req.status);
  const triage = isApprover && ["new", "needs_info", "approved"].includes(req.status);
  const canCheckOff = ["approved", "claude", "review", "shipped"].includes(req.status) && (isApprover || isRequester || isAssignee);
  const canReopen = ["done", "declined", "shipped"].includes(req.status) && (isApprover || isRequester);
  const draft = req.ai_draft;
  const gh = req.github || {};
  const events = [...(req.events || [])].reverse();

  return (
    <div className="space-y-7 pb-10" data-testid="help-desk-detail">
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-2 text-[12px] text-muted-foreground">
          <span className="font-mono">{req.ref}</span>
          <span>·</span>
          <span title={fullTime(req.created_at)}>Filed {fullTime(req.created_at)}</span>
        </div>
        <h2 className="text-[22px] font-light tracking-tight leading-snug">{req.title}</h2>
        <div className="flex flex-wrap items-center gap-2">
          <StatusPill status={req.status} />
          <span className="text-[12px] text-muted-foreground">{STATUS[req.status]?.hint}</span>
        </div>
      </div>

      {/* Facts */}
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3 border-y border-border py-4 text-[13px]">
        <div><dt className="text-[10px] uppercase tracking-[1.6px] text-muted-foreground">Requested by</dt><dd>{req.requester?.name}</dd></div>
        <div><dt className="text-[10px] uppercase tracking-[1.6px] text-muted-foreground">Owner</dt><dd>{req.assignee?.name || (req.status === "claude" || req.status === "review" ? "Claude" : "Unassigned")}</dd></div>
        <div><dt className="text-[10px] uppercase tracking-[1.6px] text-muted-foreground">Type</dt><dd>{CATEGORY[req.category] || req.category}</dd></div>
        <div><dt className="text-[10px] uppercase tracking-[1.6px] text-muted-foreground">How soon</dt><dd>{URGENCY[req.urgency] || req.urgency}</dd></div>
        {req.area && <div><dt className="text-[10px] uppercase tracking-[1.6px] text-muted-foreground">Part of HQ</dt><dd>{req.area}</dd></div>}
        {req.page_url && (
          <div><dt className="text-[10px] uppercase tracking-[1.6px] text-muted-foreground">Filed from</dt>
            <dd><a href={req.page_url} className="text-estate-700 underline-offset-2 hover:underline font-mono text-[12px]">{req.page_url}</a></dd></div>
        )}
        {req.done_at && <div><dt className="text-[10px] uppercase tracking-[1.6px] text-muted-foreground">{req.status === "declined" ? "Declined" : "Done"}</dt><dd>{fullTime(req.done_at)}</dd></div>}
      </dl>

      {/* The request */}
      <section className="space-y-3">
        <p className="whitespace-pre-wrap text-[15px] leading-relaxed">{req.details}</p>
        {req.why && <p className="text-[14px] text-muted-foreground whitespace-pre-wrap"><span className="text-foreground">Why it matters: </span>{req.why}</p>}
        <div className="flex flex-wrap gap-2 items-end">
          {(req.attachments || []).map((a) => <Attachment key={a.id} requestId={req.id} att={a} />)}
          {open && (isRequester || isApprover || isAssignee) && (
            <label className="inline-flex items-center gap-1.5 text-[13px] text-estate-700 cursor-pointer">
              <Paperclip className="w-3.5 h-3.5" /> Add file
              <input type="file" multiple className="hidden" onChange={(e) => { attach(e.target.files); e.target.value = ""; }} />
            </label>
          )}
        </div>
      </section>

      {/* GitHub progress */}
      {(gh.issue_url || gh.pr_url) && (
        <section className="flex flex-wrap items-center gap-3 rounded-lg border border-estate-200 bg-estate-50 px-4 py-3 text-[13px]">
          <GitPullRequest className="w-4 h-4 text-estate-700" />
          {gh.pr_url ? (
            <a href={gh.pr_url} target="_blank" rel="noreferrer" className="text-estate-700 hover:underline inline-flex items-center gap-1">
              Pull request #{gh.pr_number} · {gh.pr_state === "merged" ? "merged" : gh.pr_state === "open" ? "waiting for review" : gh.pr_state}
              <ExternalLink className="w-3 h-3" />
            </a>
          ) : (
            <span className="text-estate-700">Claude is on it — no pull request yet</span>
          )}
          {gh.issue_url && (
            <a href={gh.issue_url} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
              Work log <ExternalLink className="w-3 h-3" />
            </a>
          )}
          {["claude", "review", "shipped"].includes(req.status) && (
            <Button variant="ghost" size="sm" className="ml-auto h-7" onClick={sync} disabled={busy === "sync"}>
              <RefreshCw className={`w-3.5 h-3.5 mr-1 ${busy === "sync" ? "animate-spin" : ""}`} /> Refresh
            </Button>
          )}
        </section>
      )}

      {/* Approver triage */}
      {triage && (
        <section className="space-y-4 rounded-xl border border-border p-4" data-testid="help-desk-triage">
          <div className="flex items-center justify-between">
            <h3 className="text-[10px] uppercase tracking-[1.8px] text-muted-foreground">Review</h3>
            {draft?.handled_by && (
              <span className="text-[12px] text-muted-foreground">
                Claude suggests: {draft.handled_by === "claude" ? "it can make this change" : "a person should handle this"}
              </span>
            )}
          </div>

          {draft ? (
            <div className="space-y-2 text-[14px]">
              <p className="flex gap-2"><Sparkles className="w-4 h-4 mt-0.5 shrink-0 text-estate-700" /><span>{draft.summary}</span></p>
              {draft.questions?.length > 0 && (
                <div className="pl-6">
                  <p className="text-[12px] text-muted-foreground mb-1">Open questions</p>
                  <ul className="list-disc pl-4 space-y-0.5 text-[13px]">
                    {draft.questions.map((q, i) => <li key={i}>{q}</li>)}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <p className="text-[13px] text-muted-foreground flex items-center gap-2">
              {Date.now() - new Date(req.created_at).getTime() < 3 * 60 * 1000
                ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Claude is reading the request…</>
                : "No draft from Claude yet."}
            </p>
          )}

          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="hd-brief">Brief <span className="text-muted-foreground font-normal">— what gets done; edit freely</span></Label>
              <button type="button" onClick={redraft} disabled={busy === "draft"}
                className="text-[12px] text-estate-700 inline-flex items-center gap-1 disabled:opacity-50">
                <Sparkles className="w-3 h-3" /> {busy === "draft" ? "Drafting…" : "Redraft with Claude"}
              </button>
            </div>
            <Textarea id="hd-brief" rows={8} value={brief} data-testid="hd-brief"
              onChange={(e) => { setBrief(e.target.value); setBriefDirty(true); }}
              placeholder="Goal, what to change, and how we'll know it's done." className="text-[14px] font-mono leading-relaxed" />
            {briefDirty && (
              <div className="flex justify-end">
                <Button variant="ghost" size="sm" onClick={saveBrief} disabled={busy === "brief"}>Save brief</Button>
              </div>
            )}
          </div>

          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <Button onClick={() => act("send_to_claude")} disabled={!!busy || !settings?.claude_connected || !brief.trim()}
              title={settings?.claude_connected ? "Claude makes the change and opens a pull request for your review" : "Claude isn't connected yet — see the Help Desk setup note"}
              data-testid="hd-send-claude">
              <Sparkles className="w-4 h-4 mr-1.5" /> {busy === "send_to_claude" ? "Sending…" : "Approve & send to Claude"}
            </Button>
            <span className="text-[12px] text-muted-foreground sm:ml-1">or hand it to a person:</span>
          </div>
          {!settings?.claude_connected && (
            <p className="text-[12px] text-muted-foreground -mt-2">Claude isn't connected yet (HQ needs its GitHub token), so this goes to a person for now.</p>
          )}

          <div className="flex flex-col sm:flex-row gap-2">
            <Select value={assigneeId || "none"} onValueChange={(v) => setAssigneeId(v === "none" ? "" : v)}>
              <SelectTrigger className="sm:w-[240px]" data-testid="hd-assignee"><SelectValue placeholder="Choose who owns it" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No owner yet</SelectItem>
                {people.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={() => act("approve", { assignee_id: assigneeId || null })} disabled={!!busy} data-testid="hd-approve">
              <Check className="w-4 h-4 mr-1.5" /> {req.status === "approved" ? "Update owner" : "Approve"}
            </Button>
          </div>

          <div className="space-y-2 border-t border-border pt-4">
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} data-testid="hd-note"
              placeholder="Question for the requester, or why it's declined" className="text-[14px]" />
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => act("ask", { note })} disabled={!!busy || !note.trim()} data-testid="hd-ask">
                Ask the requester
              </Button>
              <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => act("decline", { note })} disabled={!!busy} data-testid="hd-decline">
                Decline
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Read-only brief once it's out of triage */}
      {!triage && req.brief && (isApprover || isAssignee) && (
        <section className="space-y-1.5">
          <h3 className="text-[10px] uppercase tracking-[1.8px] text-muted-foreground">Brief</h3>
          <p className="whitespace-pre-wrap text-[13px] font-mono leading-relaxed text-muted-foreground">{req.brief}</p>
        </section>
      )}

      {/* Check off / reopen */}
      {(canCheckOff || canReopen) && (
        <section className="flex flex-wrap items-center gap-3">
          {canCheckOff && (
            <Button onClick={() => act("done")} disabled={!!busy} data-testid="hd-done">
              <Check className="w-4 h-4 mr-1.5" /> {req.status === "shipped" && isRequester ? "It works — mark done" : "Mark done"}
            </Button>
          )}
          {canReopen && (
            <Button variant="ghost" onClick={() => act("reopen")} disabled={!!busy} data-testid="hd-reopen">
              <RotateCcw className="w-4 h-4 mr-1.5" /> {req.status === "shipped" ? "Not working — reopen" : "Reopen"}
            </Button>
          )}
        </section>
      )}

      {/* Conversation + activity */}
      <section className="space-y-4">
        <h3 className="text-[10px] uppercase tracking-[1.8px] text-muted-foreground">Activity</h3>
        <div className="space-y-2">
          <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="hd-comment"
            placeholder={req.status === "needs_info" && isRequester ? "Answer the question — it goes back to the inbox" : "Add a comment"}
            className="text-[14px]"
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) sendComment(); }} />
          <div className="flex justify-end">
            <Button size="sm" variant="outline" onClick={sendComment} disabled={busy === "comment" || !comment.trim()} data-testid="hd-comment-send">
              {req.status === "needs_info" && isRequester ? "Send answer" : "Comment"}
            </Button>
          </div>
        </div>
        <ol className="space-y-0 border-l border-border ml-1.5">
          {events.map((ev) => {
            const isComment = ev.kind === "comment" || ev.kind === "asked" || ev.kind === "claude_reply";
            const fromClaude = ev.kind.startsWith("claude") || ev.kind === "shipped" || ev.kind === "pr_closed";
            return (
              <li key={ev.id} className="relative pl-5 pb-4">
                <span className={`absolute -left-[4.5px] top-1.5 h-2 w-2 rounded-full ${fromClaude ? "bg-estate-700" : "bg-border"}`} />
                <div className="text-[12px] text-muted-foreground">
                  <span className="text-foreground">{ev.by?.name || "HQ"}</span>{" "}
                  {ev.kind === "comment" ? "commented" : EVENT_VERB[ev.kind] || ev.kind}{" · "}
                  <span title={fullTime(ev.at)}>{ago(ev.at)}</span>
                  {ev.link && (
                    <a href={ev.link} target="_blank" rel="noreferrer" className="ml-2 inline-flex items-center gap-0.5 text-estate-700 hover:underline">
                      open <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
                {ev.text && (isComment || ev.kind === "declined" || ev.kind === "claude_failed" || ev.kind === "claude_branch") ? (
                  <p className="mt-1 whitespace-pre-wrap text-[14px] leading-relaxed">{ev.text}</p>
                ) : ev.text && !["created"].includes(ev.kind) ? (
                  <p className="mt-0.5 text-[13px] text-muted-foreground">{ev.text}</p>
                ) : null}
              </li>
            );
          })}
        </ol>
      </section>
    </div>
  );
}
